"use client";

import { Dialog as DialogPrimitive } from "@base-ui/react/dialog";
import { Drawer as DrawerBasePrimitive } from "@base-ui/react/drawer";
import * as React from "react";

import { Button } from "@beweegbaas/ui/components/ui/button";
import { Drawer, DrawerContent } from "@beweegbaas/ui/components/ui/drawer";
import { useIsMobile } from "@beweegbaas/ui/hooks/use-mobile";
import { cn, cnState } from "@beweegbaas/ui/lib/utils";
import { IconX } from "@tabler/icons-react";

// On mobile a Dialog renders as a bottom Drawer; subcomponents read this
// context to pick the matching primitive tree.
const DialogAsDrawerContext = React.createContext(false);

function Dialog({ ...props }: DialogPrimitive.Root.Props) {
  const isMobile = useIsMobile();
  return (
    <DialogAsDrawerContext.Provider value={isMobile}>
      {isMobile ? (
        <Drawer data-slot="dialog" {...(props as DrawerBasePrimitive.Root.Props)} />
      ) : (
        <DialogPrimitive.Root data-slot="dialog" {...props} />
      )}
    </DialogAsDrawerContext.Provider>
  );
}

function DialogTrigger({ ...props }: DialogPrimitive.Trigger.Props) {
  const asDrawer = React.useContext(DialogAsDrawerContext);
  if (asDrawer) {
    return (
      <DrawerBasePrimitive.Trigger
        data-slot="dialog-trigger"
        {...(props as DrawerBasePrimitive.Trigger.Props)}
      />
    );
  }
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />;
}

function DialogPortal({ ...props }: DialogPrimitive.Portal.Props) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />;
}

function DialogClose({ ...props }: DialogPrimitive.Close.Props) {
  const asDrawer = React.useContext(DialogAsDrawerContext);
  if (asDrawer) {
    return (
      <DrawerBasePrimitive.Close
        data-slot="dialog-close"
        {...(props as DrawerBasePrimitive.Close.Props)}
      />
    );
  }
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />;
}

function DialogOverlay({ className, ...props }: DialogPrimitive.Backdrop.Props) {
  return (
    <DialogPrimitive.Backdrop
      data-slot="dialog-overlay"
      className={cnState(
        `data-open:animate-in data-closed:animate-out data-closed:fade-out-0
        data-open:fade-in-0 fixed inset-0 isolate z-50 bg-black/10 duration-100
        supports-backdrop-filter:backdrop-blur-xs dark:bg-black/50`,
        className,
      )}
      {...props}
    />
  );
}

function DialogContent({
  className,
  children,
  showCloseButton = true,
  overlayClassName,
  ...props
}: DialogPrimitive.Popup.Props & {
  showCloseButton?: boolean;
  overlayClassName?: string;
}) {
  const asDrawer = React.useContext(DialogAsDrawerContext);
  if (asDrawer) {
    // Swipe handle replaces the close button on mobile
    return (
      <DrawerContent
        data-slot="dialog-content"
        innerClassName={cn("grid gap-6 p-6 pt-4 text-sm", className)}
        {...(props as DrawerBasePrimitive.Popup.Props)}
      >
        {children}
      </DrawerContent>
    );
  }
  return (
    <DialogPortal>
      <DialogOverlay className={overlayClassName} />

      <DialogPrimitive.Popup
        data-slot="dialog-content"
        className={cnState(
          [
            `bg-background data-open:animate-in data-closed:animate-out
            data-closed:fade-out-0 data-open:fade-in-0 data-closed:zoom-out-95
            data-open:zoom-in-95 ring-foreground/10 fixed left-1/2 z-50 grid w-full
            -translate-x-1/2 gap-6 rounded-xl p-6
            text-sm ring-1 duration-100 outline-none max-md:bottom-4 md:top-1/2 md:-translate-y-1/2
            `,
            "max-h-[100svh] max-w-[min(1500px,calc(100vw-2rem))] overflow-y-auto",
          ],
          className,
        )}
        {...props}
      >
        {showCloseButton && (
          <DialogPrimitive.Close
            data-slot="dialog-close"
            render={
              <Button variant="light" className="fixed top-3 right-3 z-90 size-8" size="icon-sm" />
            }
          >
            <IconX />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        )}
        {children}
      </DialogPrimitive.Popup>
    </DialogPortal>
  );
}

function DialogHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div data-slot="dialog-header" className={cn("flex flex-col gap-2", className)} {...props} />
  );
}

function DialogFooter({
  className,
  showCloseButton = false,
  children,
  ...props
}: React.ComponentProps<"div"> & {
  showCloseButton?: boolean;
}) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn("flex flex-col-reverse gap-2 sm:flex-row sm:justify-end", className)}
      {...props}
    >
      {children}
      {showCloseButton && <DialogClose render={<Button variant="outline" />}>Close</DialogClose>}
    </div>
  );
}

function DialogTitle({ className, ...props }: DialogPrimitive.Title.Props) {
  const asDrawer = React.useContext(DialogAsDrawerContext);
  const titleClassName = cnState("mb-0 font-sans text-base leading-none font-normal", className);
  if (asDrawer) {
    return (
      <DrawerBasePrimitive.Title
        data-slot="dialog-title"
        className={titleClassName}
        {...(props as DrawerBasePrimitive.Title.Props)}
      />
    );
  }
  return <DialogPrimitive.Title data-slot="dialog-title" className={titleClassName} {...props} />;
}

function DialogDescription({ className, ...props }: DialogPrimitive.Description.Props) {
  const asDrawer = React.useContext(DialogAsDrawerContext);
  const descriptionClassName = cnState(
    `text-muted-foreground *:[a]:hover:text-foreground text-sm
    *:[a]:underline *:[a]:underline-offset-3`,
    className,
  );
  if (asDrawer) {
    return (
      <DrawerBasePrimitive.Description
        data-slot="dialog-description"
        className={descriptionClassName}
        {...(props as DrawerBasePrimitive.Description.Props)}
      />
    );
  }
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={descriptionClassName}
      {...props}
    />
  );
}

export {
  Dialog,
  DialogClose,
  DialogPrimitive,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
};
